import { useEffect, useState } from 'react';
import { ApiError } from '../api';
import { linkProps } from '../router';
import { ago, DECISION, hours, mbps, tautulliApi, type TActivity, type THistoryRow, type TLibrary, type TOverview, type TPopular, type TSession, type TUser } from '../tautulli';

const TABS: [string, string][] = [['active', 'Nu actief'], ['overview', 'Overzicht'], ['popular', 'Populair'], ['history', 'Geschiedenis'], ['users', 'Gebruikers'], ['libraries', 'Bibliotheken']];
const errText = (e: unknown, fb: string) => (e instanceof ApiError && e.message !== e.code ? e.message : fb);

/** Server-pagina (Tautulli), alleen voor de beheerder. */
export function ServerPage({ tab }: { tab: string }) {
  const current = TABS.find(([id]) => id === tab)?.[0] ?? 'active';
  return (
    <main className="page server-page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Server · Tautulli</span>
          <h1>{TABS.find(([id]) => id === current)?.[1]}</h1>
        </div>
        <nav className="filterbar srv-tabs" aria-label="Onderdelen" data-focus-group>
          {TABS.map(([id, label]) => (
            <a key={id} className="chip" aria-pressed={id === current} aria-current={id === current ? 'page' : undefined} {...linkProps(`/server/${id}`)}>{label}</a>
          ))}
        </nav>
      </header>
      {current === 'active' && <Active />}
      {current === 'overview' && <Overview />}
      {current === 'popular' && <Popular />}
      {current === 'history' && <History />}
      {current === 'users' && <Users />}
      {current === 'libraries' && <Libraries />}
      <div className="footer-space" />
    </main>
  );
}

function useLoad<T>(fn: () => Promise<T>, deps: unknown[], every?: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    const run = () => fn().then((d) => !off && (setData(d), setError(null))).catch((e: unknown) => !off && setError(errText(e, 'Tautulli is niet bereikbaar.')));
    void run();
    const t = every ? window.setInterval(run, every) : undefined;
    return () => {
      off = true;
      window.clearInterval(t);
    };
  }, deps);
  return { data, error };
}

function Err({ error }: { error: string }) {
  return <p className="placeholder-row error" role="alert">{error}{/gekoppeld/.test(error) ? <> — koppel Tautulli bij <a {...linkProps('/settings/system')}>Instellingen → Systeem</a>.</> : null}</p>;
}

// ---------- Nu actief ----------

function Active() {
  const { data, error } = useLoad<TActivity | null>(() => tautulliApi.activity(), [], 10_000);
  const [message, setMessage] = useState('De server gaat even in onderhoud, over 10 minuten weer terug.');
  if (error) return <Err error={error} />;
  if (!data) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  const t = data.totals;
  return (
    <>
      <div className="srv-stats">
        <Stat label="Streams nu" value={String(t.streams)} hint={`${t.directPlay} direct · ${t.directStream} direct stream · ${t.transcode} transcode`} />
        <Stat label="Bandbreedte" value={mbps(t.bandwidth)} hint={`LAN ${mbps(t.lan)} · WAN ${mbps(t.wan)}`} />
        <Stat label="Transcode" value={String(t.transcode)} hint={data.streams.some((s) => s.decision === 'transcode' && s.hw) ? 'met hardware' : t.transcode ? 'software' : 'geen'} tone={t.transcode ? 'warn' : undefined} />
      </div>
      {!data.streams.length && <p className="placeholder-row">Er wordt nu niets afgespeeld.</p>}
      <div className="srv-sessions">
        {data.streams.map((s) => <SessionCard key={s.key} s={s} message={message} />)}
      </div>
      {data.streams.length > 0 && (
        <label className="srv-message">
          <span>Bericht bij stoppen</span>
          <input className="text-input" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={300} />
          <span className="subtle">De kijker ziet dit bericht in Plex.</span>
        </label>
      )}
    </>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warn' | 'ok' }) {
  return (
    <div className={`ua-stat${tone ? ` srv-${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint && <span className="subtle srv-hint">{hint}</span>}
    </div>
  );
}

function SessionCard({ s, message }: { s: TSession; message: string }) {
  const [confirm, setConfirm] = useState(false);
  const [state, setState] = useState<string | null>(null);
  const d = DECISION[s.decision];
  const left = Math.max(0, s.duration - s.offset);
  const stop = async () => {
    setState('Stoppen…');
    try {
      await tautulliApi.terminate(s.key, s.id, message);
      setState('✓ Gestopt');
    } catch (e) {
      setState(errText(e, 'Stoppen mislukt'));
    }
  };
  return (
    <article className="srv-session">
      <div className="srv-poster" style={s.poster ? { backgroundImage: `url("${s.poster}")` } : undefined} />
      <div className="srv-body">
        <div className="srv-who">
          {s.userThumb ? <img src={s.userThumb} alt="" className="srv-avatar" /> : <span className="srv-avatar" aria-hidden="true" />}
          <strong>{s.user}</strong>
          <span className="subtle">· {s.player}{s.location ? ` · ${s.location.toUpperCase()}` : ''}</span>
          <span className={`srv-dec ${d.cls}`}>{d.label}</span>
        </div>
        <strong className="srv-title">{s.title}{s.state === 'paused' ? ' ⏸' : ''}</strong>
        {s.sub && <span className="subtle">{s.sub}</span>}
        <span className="subtle srv-tech">
          {s.from}{s.to ? ` → ${s.to}` : ''}{s.why ? ` · ${s.why}` : ''}{s.decision === 'transcode' ? (s.hw ? ' · hardware' : ' · software') : ''}
        </span>
        <div className="resume-bar"><div style={{ width: `${s.progress}%` }} /></div>
        <div className="srv-foot subtle">
          <span>{s.progress}% · nog {hours(left / 1000)}</span>
          <span>· {mbps(s.bandwidth)}{s.quality ? ` · ${s.quality}` : ''}</span>
          <span style={{ flex: 1 }} />
          {state ? <span>{state}</span> : confirm ? (
            <>
              <button className="btn btn-danger btn-sm" onClick={() => void stop()}>Ja, stoppen</button>
              <button className="btn btn-sm" onClick={() => setConfirm(false)}>Annuleren</button>
            </>
          ) : (
            <button className="btn btn-sm srv-stop" onClick={() => setConfirm(true)}>■ Stoppen…</button>
          )}
        </div>
      </div>
    </article>
  );
}

// ---------- Overzicht ----------

const RANGES: [number, string][] = [[7, '7 dagen'], [30, '30 dagen'], [90, '90 dagen'], [365, 'Dit jaar']];
const SERIES_NL: Record<string, string> = { Movies: 'Films', TV: 'Series', Music: 'Muziek', Live: 'Live TV', 'Live TV': 'Live TV' };
const SERIES_CLS: Record<string, string> = { Movies: 'bar-movies', TV: 'bar-tv', Music: 'bar-music' };

function RangePicker({ days, set }: { days: number; set: (d: number) => void }) {
  return (
    <div className="chip-wrap srv-range" data-focus-group>
      {RANGES.map(([d, l]) => <button key={d} className="chip" aria-pressed={days === d} onClick={() => set(d)}>{l}</button>)}
    </div>
  );
}

function Overview() {
  const [days, setDays] = useState(30);
  const { data, error } = useLoad<TOverview>(() => tautulliApi.overview(days), [days]);
  if (error) return <Err error={error} />;
  const g = data?.byDate;
  const totals = g ? g.categories.map((_c, i) => g.series.reduce((a, s) => a + (s.data[i] ?? 0), 0)) : [];
  const max = Math.max(1, ...totals);
  const st = data?.streamTypes;
  const sum = st ? st.directPlay + st.directStream + st.transcode : 0;
  const pct = (n: number) => (sum ? Math.round((n / sum) * 100) : 0);
  // Bij veel dagen: kolommen samenvoegen tot weken
  const step = g && g.categories.length > 60 ? 7 : 1;
  const cols = g ? Array.from({ length: Math.ceil(g.categories.length / step) }, (_x, k) => ({
    label: g.categories[k * step],
    parts: g.series.map((s) => ({ name: s.name, v: s.data.slice(k * step, k * step + step).reduce((a, b) => a + b, 0) })),
  })) : [];
  const colMax = Math.max(1, ...cols.map((c) => c.parts.reduce((a, p) => a + p.v, 0)));
  return (
    <>
      <RangePicker days={days} set={setDays} />
      {!data ? <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} /> : (
        <div className="srv-grid2">
          <section className="ua-block srv-chart" aria-label="Afspeelacties per dag">
            <div className="srv-chart-head">
              <h2>Afspeelacties per {step === 7 ? 'week' : 'dag'}</h2>
              <span className="subtle">{totals.reduce((a, b) => a + b, 0)} in totaal</span>
              <span style={{ flex: 1 }} />
              {g?.series.map((s) => <span key={s.name} className="srv-legend"><i className={SERIES_CLS[s.name] ?? 'bar-other'} />{SERIES_NL[s.name] ?? s.name}</span>)}
            </div>
            <div className="srv-bars" role="img" aria-label={`Afspeelacties, hoogste ${max} per dag`}>
              {cols.map((c) => {
                const tot = c.parts.reduce((a, p) => a + p.v, 0);
                return (
                  <div key={c.label} className="srv-bar" title={`${c.label}: ${tot}`}>
                    {c.parts.map((p) => <span key={p.name} className={SERIES_CLS[p.name] ?? 'bar-other'} style={{ height: `${(p.v / colMax) * 100}%` }} />)}
                  </div>
                );
              })}
            </div>
            <div className="srv-axis subtle"><span>{cols[0]?.label}</span><span>{cols[Math.floor(cols.length / 2)]?.label}</span><span>{cols[cols.length - 1]?.label}</span></div>
          </section>
          <section className="ua-block" aria-label="Hoe er wordt afgespeeld">
            <h2>Hoe er wordt afgespeeld</h2>
            <div className="srv-split" aria-hidden="true">
              <span className="dec-bg-dp" style={{ width: `${pct(st!.directPlay)}%` }} />
              <span className="dec-bg-ds" style={{ width: `${pct(st!.directStream)}%` }} />
              <span className="dec-bg-tc" style={{ width: `${pct(st!.transcode)}%` }} />
            </div>
            <ul className="srv-list">
              <li><i className="dec-bg-dp" />Direct Play · {pct(st!.directPlay)}% ({st!.directPlay})</li>
              <li><i className="dec-bg-ds" />Direct Stream · {pct(st!.directStream)}% ({st!.directStream})</li>
              <li><i className="dec-bg-tc" />Transcode · {pct(st!.transcode)}% ({st!.transcode})</li>
            </ul>
            <h2>Drukste momenten</h2>
            <p>{[data.busiestDay ? dayNl(data.busiestDay) : null, data.busiestHour ? `rond ${data.busiestHour}:00` : null].filter(Boolean).join(' · ') || '—'}</p>
          </section>
        </div>
      )}
    </>
  );
}

const DAYS_NL: Record<string, string> = { Sunday: 'zondag', Monday: 'maandag', Tuesday: 'dinsdag', Wednesday: 'woensdag', Thursday: 'donderdag', Friday: 'vrijdag', Saturday: 'zaterdag' };
const dayNl = (d: string) => (DAYS_NL[d] ?? d).replace(/^./, (c) => c.toUpperCase());

// ---------- Populair ----------

function Popular() {
  const [days, setDays] = useState(30);
  const { data, error } = useLoad<TPopular>(() => tautulliApi.popular(days), [days]);
  if (error) return <Err error={error} />;
  return (
    <>
      <RangePicker days={days} set={setDays} />
      {!data ? <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} /> : (
        <div className="srv-grid3">
          <TopList title="Meest bekeken films" rows={data.movies.map((m) => ({ key: m.title, name: m.title, sub: m.year ? String(m.year) : null, value: `${m.plays}×`, img: m.poster, href: m.ratingKey ? `/item/${m.ratingKey}` : null }))} />
          <TopList title="Meest bekeken series" rows={data.shows.map((m) => ({ key: m.title, name: m.title, sub: m.users ? `${m.users} kijkers` : null, value: `${m.plays} afl.`, img: m.poster, href: m.ratingKey ? `/item/${m.ratingKey}` : null }))} />
          <TopList title="Actiefste gebruikers" round rows={data.users.map((u) => ({ key: u.name, name: u.name, sub: `${u.plays} keer`, value: hours(u.duration), img: u.thumb, href: null }))} />
          {data.music.length > 0 && <TopList title="Meest beluisterd" rows={data.music.map((m) => ({ key: m.title, name: m.title, sub: null, value: `${m.plays}×`, img: m.poster, href: null }))} />}
          <TopList title="Platforms" rows={data.platforms.map((p) => ({ key: p.name, name: p.name, sub: hours(p.duration), value: `${p.plays}×`, img: null, href: null }))} />
        </div>
      )}
    </>
  );
}

function TopList({ title, rows, round }: { title: string; rows: { key: string; name: string; sub: string | null; value: string; img: string | null; href: string | null }[]; round?: boolean }) {
  return (
    <section className="ua-block" aria-label={title}>
      <h2>{title}</h2>
      {!rows.length && <p className="subtle">Nog niets.</p>}
      <ol className="srv-top">
        {rows.slice(0, 10).map((r, i) => {
          const inner = (
            <>
              <span className="srv-rank">{i + 1}</span>
              {r.img !== undefined && <span className={`srv-thumb${round ? ' round' : ''}`} style={r.img ? { backgroundImage: `url("${r.img}")` } : undefined} />}
              <span className="srv-name"><strong>{r.name}</strong>{r.sub && <span className="subtle">{r.sub}</span>}</span>
              <span className="subtle">{r.value}</span>
            </>
          );
          return <li key={`${r.key}:${i}`}>{r.href ? <a className="srv-top-row" {...linkProps(r.href)}>{inner}</a> : <div className="srv-top-row">{inner}</div>}</li>;
        })}
      </ol>
    </section>
  );
}

// ---------- Geschiedenis ----------

function History() {
  const [rows, setRows] = useState<THistoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [user, setUser] = useState<number | undefined>();
  const [users, setUsers] = useState<TUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (start: number) => {
    setBusy(true);
    try {
      const r = await tautulliApi.history(start, user);
      setTotal(r.total);
      setRows((prev) => (start ? [...prev, ...r.rows] : r.rows));
    } catch (e) {
      setError(errText(e, 'Laden mislukt.'));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(0);
  }, [user]);
  useEffect(() => {
    tautulliApi.users().then((r) => setUsers(r.users)).catch(() => setUsers([]));
  }, []);

  if (error) return <Err error={error} />;
  return (
    <>
      <div className="chip-wrap srv-range">
        <label className="ed-field srv-user-pick">Gebruiker
          <select value={user ?? ''} onChange={(e) => setUser(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">Iedereen</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
        <span className="subtle">{total} keer afgespeeld</span>
      </div>
      <div className="ua-block">
        <div className="ua-table" role="table" aria-label="Geschiedenis">
          <div className="ua-tr srv-hist ua-th" role="row"><span>Wanneer</span><span>Wie</span><span>Wat</span><span>Waarop</span><span>Afspelen</span><span>Bekeken</span></div>
          {rows.map((h, i) => (
            <div key={`${h.at}:${i}`} className="ua-tr srv-hist" role="row">
              <span className="subtle">{new Date(h.at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              <span>{h.user}</span>
              <span className="srv-name-cell">{h.title}</span>
              <span className="subtle">{h.platform}</span>
              <span><span className={`srv-dec ${DECISION[h.decision]?.cls ?? ''}`}>{DECISION[h.decision]?.label ?? h.decision}</span></span>
              <span className="subtle">{h.percent}% · {hours(h.watched)}</span>
            </div>
          ))}
        </div>
        {rows.length < total && <div className="form-actions"><button className="btn" disabled={busy} onClick={() => void load(rows.length)}>{busy ? 'Laden…' : 'Meer laden'}</button></div>}
      </div>
    </>
  );
}

// ---------- Gebruikers en bibliotheken ----------

function Users() {
  const { data, error } = useLoad(() => tautulliApi.users(), []);
  if (error) return <Err error={error} />;
  if (!data) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  return (
    <div className="ua-block">
      <div className="ua-table" role="table" aria-label="Gebruikers">
        <div className="ua-tr srv-users ua-th" role="row"><span>Gebruiker</span><span>Laatst gezien</span><span>Laatst bekeken</span><span>Apparaat</span><span>Keer</span><span>Totaal</span></div>
        {data.users.map((u) => (
          <div key={u.id} className="ua-tr srv-users" role="row">
            <span className="ua-name">{u.thumb ? <img src={u.thumb} alt="" className="ua-avatar" /> : <span className="ua-avatar srv-avatar" />}{u.name}</span>
            <span className="subtle">{ago(u.lastSeen)}</span>
            <span className="srv-name-cell">{u.lastPlayed ?? '—'}</span>
            <span className="subtle">{u.device ?? '—'}</span>
            <span>{u.plays}</span>
            <span>{hours(u.duration)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Libraries() {
  const { data, error } = useLoad(() => tautulliApi.libraries(), []);
  if (error) return <Err error={error} />;
  if (!data) return <div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} />;
  const label = (l: TLibrary) => (l.type === 'movie' ? `${l.count} films` : l.type === 'show' ? `${l.count} series · ${l.children} afleveringen` : l.type === 'artist' ? `${l.count} artiesten · ${l.children} nummers` : `${l.count} items`);
  return (
    <div className="srv-libs">
      {data.libraries.map((l) => (
        <section key={l.id} className="ua-stat">
          <span>{l.name}</span>
          <strong>{l.count}</strong>
          <span className="subtle srv-hint">{label(l)}</span>
          <span className="subtle srv-hint">{l.plays} keer afgespeeld · {hours(l.duration)}</span>
          <span className="subtle srv-hint">Laatst: {l.lastPlayed ?? '—'} ({ago(l.lastAccessed)})</span>
        </section>
      ))}
    </div>
  );
}
